/**
 * Firebase Orders Service
 * Handles Firestore order operations
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  writeBatch,
  runTransaction,
  query,
  where,
  orderBy,
  Timestamp,
  QueryConstraint,
  arrayUnion,
} from 'firebase/firestore';
import { db, COLLECTIONS } from '@/config/firebase.config';
import { Coupon } from '@/types/coupon.types';
import {
  Order,
  OrderItem,
  OrderStatus,
  OrderFilters,
  OrderTimeline,
  PaymentStatus,
  PaymentMethod,
} from '@/types/order.types';
import { Address } from '@/types/user.types';
import { CartItem } from '@/types/cart.types';
import { generateOrderNumber } from '@/utils/formatters';

const collectionRef = collection(db, COLLECTIONS.ORDERS);

/**
 * Data required to create an order from the checkout form
 */
export interface CreateOrderData {
  userId: string | null;
  fullName: string;
  phoneNumber: string;
  email?: string;
  addressLine1: string;
  addressLine2?: string;
  landmark?: string;
  city: string;
  state: string;
  pincode: string;
  notes?: string;
  paymentMethod: PaymentMethod;
  cartItems: CartItem[];
  subtotal: number;
  shippingCost: number;
  tax: number;
  discount: number;
  total: number;
  couponCode?: string;
}

/**
 * Build a shipping Address object from flat checkout form fields
 */
export const buildShippingAddress = (data: CreateOrderData): Address => ({
  id: `addr_${Date.now()}`,
  label: 'Shipping',
  fullName: data.fullName,
  phoneNumber: data.phoneNumber,
  addressLine1: data.addressLine1,
  ...(data.addressLine2 ? { addressLine2: data.addressLine2 } : {}),
  ...(data.landmark ? { landmark: data.landmark } : {}),
  city: data.city,
  state: data.state,
  pincode: data.pincode,
  isDefault: false,
});

/**
 * Map cart items to order items, capturing price at time of purchase
 */
export const mapCartToOrderItems = (cartItems: CartItem[]): OrderItem[] =>
  cartItems.map((item) => ({
    productId: item.product.id,
    productName: item.product.name,
    productThumbnail: item.product.thumbnail || '',
    productSlug: item.product.slug || '',
    quantity: item.quantity,
    priceAtTime: item.product.price,
    sku: item.product.sku || '',
    ...(item.selectedSize ? { selectedSize: item.selectedSize } : {}),
  }));

/**
 * Create a new order in Firestore.
 *
 * When a coupon is applied, the order creation and the coupon's usage
 * increment happen atomically inside a single transaction, so the coupon is
 * only ever consumed when the order is actually placed. If the coupon is no
 * longer redeemable at this point, the whole transaction fails and no order
 * is created.
 */
export const createOrder = async (data: CreateOrderData): Promise<Order> => {
  const orderNumber = generateOrderNumber();
  const now = Timestamp.now();

  const initialTimeline: OrderTimeline = {
    status: OrderStatus.PENDING,
    timestamp: now,
    note: 'Order placed',
  };

  const items = mapCartToOrderItems(data.cartItems);
  const shippingAddress = buildShippingAddress(data);

  const orderDoc = {
    orderNumber,
    userId: data.userId,
    items,
    shippingAddress,
    subtotal: data.subtotal,
    shippingCost: data.shippingCost,
    tax: data.tax,
    discount: data.discount,
    total: data.total,
    status: OrderStatus.PENDING,
    paymentStatus: PaymentStatus.PENDING,
    paymentMethod: data.paymentMethod,
    timeline: [initialTimeline],
    createdAt: now,
    updatedAt: now,
    ...(data.couponCode ? { couponCode: data.couponCode } : {}),
    ...(data.email ? { guestEmail: data.email } : {}),
    ...(!data.userId ? { guestPhone: data.phoneNumber } : {}),
    ...(data.notes ? { notes: data.notes } : {}),
  };

  const orderRef = doc(collectionRef);
  const lookupRef = doc(db, COLLECTIONS.ORDER_LOOKUPS, orderNumber);

  if (data.couponCode) {
    const couponRef = doc(db, COLLECTIONS.COUPONS, data.couponCode);

    await runTransaction(db, async (txn) => {
      // Reads first (Firestore requires all reads before writes)
      const couponSnap = await txn.get(couponRef);
      if (!couponSnap.exists()) {
        throw new Error('Coupon is no longer available');
      }
      const coupon = { id: couponSnap.id, ...couponSnap.data() } as Coupon;

      if (!coupon.isActive) {
        throw new Error('Coupon is no longer active');
      }
      if (coupon.expiresAt && coupon.expiresAt.toMillis() < Date.now()) {
        throw new Error('Coupon has expired');
      }
      if (coupon.usageCount >= coupon.usageLimit) {
        throw new Error('Coupon has already been used');
      }
      if (coupon.minOrderValue != null && data.subtotal < coupon.minOrderValue) {
        throw new Error('Order no longer meets the coupon minimum');
      }

      // Writes
      txn.set(orderRef, orderDoc);
      txn.set(lookupRef, { orderId: orderRef.id, phone: data.phoneNumber });
      txn.update(couponRef, {
        usageCount: coupon.usageCount + 1,
        updatedAt: now,
      });
    });

    return { id: orderRef.id, ...orderDoc } as Order;
  }

  const batch = writeBatch(db);
  batch.set(orderRef, orderDoc);
  batch.set(lookupRef, {
    orderId: orderRef.id,
    phone: data.phoneNumber,
  });
  await batch.commit();

  return { id: orderRef.id, ...orderDoc } as Order;
};

/**
 * Get a single order by its document ID
 */
export const getOrderById = async (orderId: string): Promise<Order | null> => {
  const docSnap = await getDoc(doc(db, COLLECTIONS.ORDERS, orderId));
  if (!docSnap.exists()) return null;
  return { id: docSnap.id, ...docSnap.data() } as Order;
};

/**
 * Get all orders for a specific user, sorted newest first
 */
export const getOrdersByUserId = async (userId: string): Promise<Order[]> => {
  const q = query(collectionRef, where('userId', '==', userId));
  const snapshot = await getDocs(q);
  const orders = snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Order));
  return orders.sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis());
};

/**
 * Get all orders with optional filters (admin use)
 */
export const getAllOrders = async (filters?: OrderFilters): Promise<Order[]> => {
  const constraints: QueryConstraint[] = [];

  if (filters?.status) {
    constraints.push(where('status', '==', filters.status));
  }

  if (filters?.paymentStatus) {
    constraints.push(where('paymentStatus', '==', filters.paymentStatus));
  }

  constraints.push(orderBy('createdAt', 'desc'));

  const q = query(collectionRef, ...constraints);
  const snapshot = await getDocs(q);
  let orders = snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Order));

  if (filters?.dateFrom) {
    const fromMs = filters.dateFrom.getTime();
    orders = orders.filter((o) => o.createdAt.toMillis() >= fromMs);
  }

  if (filters?.dateTo) {
    const toMs = filters.dateTo.getTime();
    orders = orders.filter((o) => o.createdAt.toMillis() <= toMs);
  }

  if (filters?.search) {
    const search = filters.search.toLowerCase();
    orders = orders.filter(
      (o) =>
        o.orderNumber.toLowerCase().includes(search) ||
        o.shippingAddress.fullName.toLowerCase().includes(search) ||
        (o.guestPhone && o.guestPhone.includes(search))
    );
  }

  orders.sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis());

  return orders;
};

/**
 * Update order status and append to timeline
 */
export const updateOrderStatus = async (
  orderId: string,
  status: OrderStatus,
  note?: string
): Promise<void> => {
  const now = Timestamp.now();
  const timelineEntry: OrderTimeline = {
    status,
    timestamp: now,
    ...(note ? { note } : {}),
  };

  const baseUpdates = {
    status,
    timeline: arrayUnion(timelineEntry),
    updatedAt: now,
  };

  if (status === OrderStatus.DELIVERED) {
    await updateDoc(doc(db, COLLECTIONS.ORDERS, orderId), {
      ...baseUpdates,
      deliveredAt: now,
      paymentStatus: PaymentStatus.PAID,
    });
  } else if (status === OrderStatus.CANCELLED) {
    const orderSnap = await getDoc(doc(db, COLLECTIONS.ORDERS, orderId));
    const orderData = orderSnap.data();
    // Online paid orders: keep PAID so admin knows manual Razorpay refund is needed
    const shouldRefund =
      !orderData || orderData.paymentStatus !== PaymentStatus.PAID;
    await updateDoc(doc(db, COLLECTIONS.ORDERS, orderId), {
      ...baseUpdates,
      ...(shouldRefund ? { paymentStatus: PaymentStatus.REFUNDED } : {}),
    });
  } else if (status === OrderStatus.REFUNDED) {
    await updateDoc(doc(db, COLLECTIONS.ORDERS, orderId), {
      ...baseUpdates,
      paymentStatus: PaymentStatus.REFUNDED,
    });
  } else {
    await updateDoc(doc(db, COLLECTIONS.ORDERS, orderId), baseUpdates);
  }
};

/**
 * Partial update of order fields (tracking, admin notes, etc.)
 */
export const updateOrder = async (
  orderId: string,
  updates: Partial<Pick<Order, 'trackingNumber' | 'trackingUrl' | 'adminNotes' | 'estimatedDelivery'>>
): Promise<void> => {
  const cleanUpdates = Object.fromEntries(
    Object.entries(updates).filter(([, value]) => value !== undefined)
  );
  await updateDoc(doc(db, COLLECTIONS.ORDERS, orderId), {
    ...cleanUpdates,
    updatedAt: Timestamp.now(),
  });
};

/**
 * Get total count of orders (admin dashboard)
 */
export const getOrdersCount = async (): Promise<number> => {
  const q = query(collectionRef, orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.size;
};

/**
 * Get total revenue from non-cancelled orders (admin dashboard)
 */
export const getOrdersRevenue = async (): Promise<number> => {
  const q = query(collectionRef, orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.reduce((sum, d) => {
    const data = d.data();
    if (data.status !== OrderStatus.CANCELLED && data.status !== OrderStatus.REFUNDED) {
      return sum + (data.total || 0);
    }
    return sum;
  }, 0);
};

/**
 * Look up an order by its order number and phone number (guest tracking).
 * Uses two get operations (not list/query) so it works without auth.
 */
export const lookupOrderByNumberAndPhone = async (
  orderNumber: string,
  phone: string
): Promise<Order | null> => {
  const lookupSnap = await getDoc(doc(db, COLLECTIONS.ORDER_LOOKUPS, orderNumber));
  if (!lookupSnap.exists()) return null;

  const lookup = lookupSnap.data() as { orderId: string; phone: string };
  if (lookup.phone !== phone) return null;

  const orderSnap = await getDoc(doc(db, COLLECTIONS.ORDERS, lookup.orderId));
  if (!orderSnap.exists()) return null;
  return { id: orderSnap.id, ...orderSnap.data() } as Order;
};

/**
 * Delete order by ID
 */
export const deleteOrder = async (orderId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.ORDERS, orderId));
  } catch (error) {
    console.error('Error deleting order:', error);
    throw new Error('Failed to delete order');
  }
};
